import {
    SenAnalysisMainChartComponent,
    type SenAnalysisMainChartConfig
} from '@/features/sen-analysis/sen-analysis/sen-analysis-main-chart/sen-analysis-main-chart.component';
import type { BuildingId } from '@/interfaces/Building.interface';
import type { CoreSensor, SensorIdentifier } from '@/interfaces/Sensor.interface';
import type { UnitCode } from '@/interfaces/Unit.interface';
import type { AnyCssColor } from '@/libs/shared/ui/theming';
import { SensorsProvider } from '@/providers/sensors/sensors.provider';
import { UiIconButtonComponent } from '@/ui/icon-button/ui-icon-button';
import { UiSectionContentComponent } from '@/ui/section/section-content/ui-section-content';
import { UiSectionComponent } from '@/ui/section/ui-section';
import { UiSpinnerComponent } from '@/ui/spinner/ui-spinner.component';
import { TimeStep } from '@/utils/temporal/time-steps.interface';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { map, switchMap, type Observable } from 'rxjs';
import {
    LegacyGraphTileTimeStep,
    type AnalysisGraphTileData,
    type LegacyGraphTileSensor,
    type LegacyGraphTileTemplate
} from '../../../interfaces/Tile.interface';
import { TilesProvider } from '../../../providers/tiles/tiles.provider';
import { EditTextComponent } from '../../form-controls/controls/edit-text/edit-text';
import { SpawnableTile } from '../spawnable-tile/spawnable-tile.component';
import { AdvizeoModalService } from './../../../services/advizeo-modal.service';

@Component({
    styleUrls: ['./tile-analysis-graph.scss'],
    selector: 'tile-analysis-graph',
    templateUrl: 'tile-analysis-graph.html',
    imports: [
        UiSectionComponent,
        UiSectionContentComponent,
        EditTextComponent,
        FormsModule,
        UiIconButtonComponent,
        AsyncPipe,
        SenAnalysisMainChartComponent,
        UiSpinnerComponent
    ]
})
export class TileAnalysisGraphComponent extends SpawnableTile<AnalysisGraphTileData> {
    private _advizeoModalService = inject(AdvizeoModalService);
    private _translateService = inject(TranslateService);
    private _tilesProvider = inject(TilesProvider);
    private _sensorsProvider = inject(SensorsProvider);

    realtimeAnalysisStateLikeData = computed(() => {
        return TilesProvider.generateStateLikeDataFromTileData(this.tileData);
    });

    mapRealtimeAnalysisData = toSignal(
        toObservable(this.realtimeAnalysisStateLikeData).pipe(
            switchMap(state => {
                return this.mapToMainChartConfig$(state);
            })
        )
    );

    getSensorsFromRealtimeAnalysisSensors$(realtimeAnalysisSensors: LegacyGraphTileSensor[]): Observable<CoreSensor[]> {
        const sensorIdentifiers: SensorIdentifier[] = realtimeAnalysisSensors.map(sensor => {
            return {
                systemId: sensor.systemId,
                sensorId: sensor.sensorId
            };
        });
        // /!\ This is a misuse of displayUnitCode
        return this._sensorsProvider.getSensorsBySystemIdsAndSensorIds$(sensorIdentifiers).pipe(
            map(sensors => {
                return sensors.map((sensor, i) => ({
                    ...sensor,
                    displayedUnitMeasureCode: realtimeAnalysisSensors[i].displayUnitCode as UnitCode
                }));
            })
        );
    }

    mapToMainChartConfig$(state: LegacyGraphTileTemplate): Observable<SenAnalysisMainChartConfig> {
        const realtimeAnalysisSensors: LegacyGraphTileSensor[] = Object.values(state.sensors.entities).filter(isNotNullish);
        return this.getSensorsFromRealtimeAnalysisSensors$(realtimeAnalysisSensors).pipe(
            map(businessSensors => {
                return {
                    buildingId: state.buildingId as BuildingId,
                    timeStep: this.mapRealtimeTimeStepToTimeStep(state.timeStep),
                    period: {
                        start: Temporal.PlainDate.from(state.periods.current.start!),
                        end: Temporal.PlainDate.from(state.periods.current.end!)
                    },
                    previousPeriodStart: state.periods.previous?.start ? Temporal.PlainDate.from(state.periods.previous.start) : null,
                    outsideTemperatureEnabled: state.weather,
                    previousPeriodOutsideTemperatureEnabled: state.previousWeather,
                    sensors: businessSensors.map(bs => {
                        const original = realtimeAnalysisSensors.find(s => s.sensorId === bs.sensorId && s.systemId === bs.systemId);
                        return {
                            timeseriesId: bs.timeseriesId,
                            color: original?.colorHex as AnyCssColor,
                            previousPeriodEnabled: original?.previousPeriod ?? false,
                            previousPeriodColor: (original?.previousPeriodColorHex ?? `var(--theming-color-palette-0)`) as AnyCssColor, // to avoid null value
                            unitGroupCodeToConvert: null,
                            groupId: original?.groupId ?? null
                        };
                    }),

                    compositeSen: [],
                    weatherIndicators: []
                };
            })
        );
    }

    private mapRealtimeTimeStepToTimeStep(realtimeAnalysisTimeStep: LegacyGraphTileTimeStep): TimeStep {
        switch (realtimeAnalysisTimeStep) {
            case LegacyGraphTileTimeStep.MONTH:
                return TimeStep.MONTH;
            case LegacyGraphTileTimeStep.DAY:
                return TimeStep.DAY;
            case LegacyGraphTileTimeStep.HOUR:
                return TimeStep.FINEST;
        }
    }

    deleteTile(ev: MouseEvent) {
        ev.preventDefault();
        ev.stopPropagation();

        this._advizeoModalService.openLegacyDialog({
            title: this._translateService.instant('WIDGET.HOME_TILES.DELETE_CONFIRMATION_TITLE'),
            message: this._translateService.instant('WIDGET.HOME_TILES.DELETE_CONFIRMATION_MESSAGE', { tileName: this.tileData.name }),
            buttons: [
                {
                    text: this._translateService.instant('BUTTONS.CANCEL'),
                    role: 'cancel'
                },
                {
                    text: this._translateService.instant('BUTTONS.DELETE'),
                    handler: () => {
                        this.processDeleteTile();
                    }
                }
            ]
        });
    }

    updateTileName() {
        this._tilesProvider.renameTile(this.tileData.tileId, this.tileData.name);
    }

    private processDeleteTile() {
        this._tilesProvider.deleteTile(this.tileData.tileId, this.tileData.buildingId).then(() => {
            this.deleted.emit();
        });
    }
}
